import React from 'react';

export default function LineChart({ data = [], height = 260, color = '#10b981', title }) {
  if (!data.length) {
    return (
      <div className="bg-white p-4 rounded shadow">
        {title && <h3 className="font-semibold mb-3">{title}</h3>}
        <p className="text-gray-400 text-center py-8">No data</p>
      </div>
    );
  }

  // ─── Internal coordinate system ───
  // Using 500×height so the SVG fills a normal dashboard card width.
  // All calculations happen in these units; the browser scales to fit.
  const W = 500;
  const H = height;
  const padding = { top: 20, right: 20, bottom: 32, left: 48 };
  const innerW = W - padding.left - padding.right;
  const innerH = H - padding.top - padding.bottom;

  const values = data.map(d => d.value || 0);
  const maxValue = Math.max(...values, 1);

  // Nice rounded top for the y-axis
  const niceMax = (() => {
    const raw = maxValue * 1.1;
    const pow = Math.pow(10, Math.floor(Math.log10(raw || 1)));
    return Math.ceil(raw / pow) * pow;
  })();

  const xAt = (i) =>
    padding.left + (i / Math.max(data.length - 1, 1)) * innerW;
  const yAt = (v) =>
    padding.top + innerH - (v / niceMax) * innerH;

  const points = data.map((d, i) => ({
    x: xAt(i),
    y: yAt(d.value || 0),
    ...d,
  }));

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');

  const areaPath = points.length > 0
    ? `${linePath} L ${points[points.length - 1].x.toFixed(2)} ${padding.top + innerH} ` +
      `L ${points[0].x.toFixed(2)} ${padding.top + innerH} Z`
    : '';

  // Y-axis gridlines at 0 / 25 / 50 / 75 / 100%
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="bg-white p-4 rounded shadow">
      {title && <h3 className="font-semibold mb-3 text-gray-800">{title}</h3>}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full block"
        style={{ height: `${H}px` }}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Y-axis gridlines + labels */}
        {yTicks.map((t) => {
          const y = padding.top + innerH * (1 - t);
          const label = Math.round(niceMax * t);
          return (
            <g key={t}>
              <line
                x1={padding.left}
                y1={y}
                x2={W - padding.right}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
              />
              <text
                x={padding.left - 8}
                y={y + 4}
                fontSize="11"
                fill="#9ca3af"
                textAnchor="end"
              >
                {label}
              </text>
            </g>
          );
        })}

        {/* Area + line */}
        {areaPath && <path d={areaPath} fill="url(#lineGrad)" />}
        {linePath && (
          <path
            d={linePath}
            stroke={color}
            strokeWidth="2.5"
            fill="none"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {/* Points + x labels */}
        {points.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x}
              cy={p.y}
              r="4"
              fill="white"
              stroke={color}
              strokeWidth="2"
            />
            <text
              x={p.x}
              y={H - 8}
              fontSize="11"
              fill="#4b5563"
              textAnchor="middle"
            >
              {p.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}